import { LayoutHeader } from "@/Header";
import { Dialog } from "@/Dialog";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import plusCircle from "$/plus-circle.svg";
import trashSrc from "$/trash.svg";
import { Button } from "@/Button";
import { useBusinessCompanyPhotoCreate } from "~/services/business/companyPhoto";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { validateImageFiles } from "../../../utils/fileValidation";
import { useConfirmTouch } from "../../../hooks/common/useConfirmTouch";
import { AnimatePresence, motion } from "framer-motion";
import { useLoadingStore } from "&/loading";

export const Route = createFileRoute("/business/layout/company-photo-add")({
  component: CompanyPhotoAdd,
});

function CompanyPhotoAdd() {
  const navigate = useNavigate();
  const { toggleLoading } = useLoadingStore();
  const queryClient = useQueryClient();

  // 업로드된 이미지들 상태
  const [uploadedImages, setUploadedImages] = useState<
    Array<{
      ImgID: number;
      ImgURL: string;
    }>
  >([]);

  // 실제 파일들 상태 (API 전송용)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // API 호출
  const createPhotoMutation = useBusinessCompanyPhotoCreate();

  // Dialog 상태
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showValidationErrorDialog, setShowValidationErrorDialog] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // useConfirmTouch 훅 사용
  const { bindClick, idle } = useConfirmTouch();

  // 이미지 삭제
  const handleImageDelete = (imgId: number) => {
    console.log("이미지 삭제 시작:", imgId);
    setUploadedImages((prev) => {
      const filteredImages = prev.filter((img) => img.ImgID !== imgId);
      console.log("이미지 삭제 완료:", {
        삭제전개수: prev.length,
        삭제후개수: filteredImages.length,
      });
      // uploadedFiles도 같은 인덱스로 삭제
      setUploadedFiles((prevFiles) => {
        const deletedIndex = prev.findIndex((img) => img.ImgID === imgId);
        if (deletedIndex !== -1) {
          const filteredFiles = prevFiles.filter(
            (_, index) => index !== deletedIndex
          );
          console.log("파일 삭제 완료:", {
            삭제전개수: prevFiles.length,
            삭제후개수: filteredFiles.length,
          });
          return filteredFiles;
        }
        return prevFiles;
      });
      return filteredImages;
    });
  };

  // 사진 선택 함수
  const handleImageSelect = () => {
    // 파일 입력 요소 생성
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/*";

    input.onchange = (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (files) {
        // 로딩 시작
        toggleLoading(true);

        // 이미지 파일 검증
        const { validFiles, errors } = validateImageFiles(files, 10); // 최대 10MB

        if (errors.length > 0) {
          setShowValidationErrorDialog(true);
          // 로딩 종료
          toggleLoading(false);
        }

        if (validFiles.length > 0) {
          // 기존 이미지 개수 확인 (최대 20개까지 제한)
          const currentImageCount = uploadedImages.length;
          const maxImages = 3;

          if (currentImageCount + validFiles.length > maxImages) {
            setErrorMessage(
              `이미지는 최대 ${maxImages}개까지 업로드 가능합니다. (현재: ${currentImageCount}개)`
            );
            setShowErrorDialog(true);
            // 로딩 종료
            toggleLoading(false);
            return;
          }

          // 새로운 이미지들을 처리
          const processFiles = async () => {
            const newImages: Array<{ ImgID: number; ImgURL: string }> = [];
            const newFiles: File[] = [];

            for (let i = 0; i < validFiles.length; i++) {
              const file = validFiles[i];
              const imageUrl = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                  resolve(e.target?.result as string);
                };
                reader.readAsDataURL(file);
              });

              const newImage = {
                ImgID: Date.now() + i + Math.random(), // 더 고유한 ID 생성
                ImgURL: imageUrl,
              };

              newImages.push(newImage);
              newFiles.push(file);
            }

            // 모든 파일이 처리되면 상태 업데이트 (기존 이미지에 추가)
            setUploadedImages((prev) => {
              const updatedImages = [...prev, ...newImages];
              console.log("이미지 업로드 완료:", {
                기존개수: prev.length,
                새로추가: newImages.length,
                총개수: updatedImages.length,
              });
              return updatedImages;
            });
            setUploadedFiles((prev) => {
              const updatedFiles = [...prev, ...newFiles];
              console.log("파일 업로드 완료:", {
                기존개수: prev.length,
                새로추가: newFiles.length,
                총개수: updatedFiles.length,
              });
              return updatedFiles;
            });

            // 로딩 종료
            toggleLoading(false);
          };

          processFiles();
        } else {
          // 유효한 파일이 없는 경우 로딩 종료
          toggleLoading(false);
        }
      } else {
        // 파일이 선택되지 않은 경우 로딩 종료
        toggleLoading(false);
      }

      // 파일 입력 요소 정리
      input.remove();
    };

    input.click();
  };

  return (
    <>
      <LayoutHeader
        title={"사업장 이미지 등록"}
        hasBackBtn
        hasFootPrint={false}
      />
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5]">
        <div className="flex flex-col flex-auto gap-4 px-5 pb-12 rounded-t-3xl">
          <div className="my-3">
            <h2 className="text-lg font-semibold text-gray-800">
              사업장 이미지 업로드
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              사업장을 소개할 이미지를 업로드해주세요.
            </p>
          </div>

          {/* 갤러리 목록 */}
          <div className="flex overflow-y-auto flex-col gap-2">
            {/* 이미지 개수 표시 */}
            {uploadedImages.length > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  업로드된 이미지: {uploadedImages.length}개
                </span>
                <span className="text-xs text-gray-400">
                  최대 3개까지 업로드 가능
                </span>
              </div>
            )}

            <div className="grid grid-cols-4 gap-1 sm:grid-cols-4">
              {/* 업로드된 이미지들 */}
              {uploadedImages.map((data) => (
                <div
                  key={data.ImgID}
                  className="overflow-hidden relative bg-white rounded-lg cursor-pointer aspect-square"
                  onClick={bindClick(() => handleImageDelete(data.ImgID))}
                >
                  <img
                    src={data.ImgURL}
                    alt={`Uploaded image ${data.ImgID}`}
                    className="object-cover w-full h-full rounded-lg"
                  />

                  {/* 삭제 오버레이 (useConfirmTouch 사용) */}
                  <AnimatePresence>
                    {idle && (
                      <motion.div
                        className="flex absolute inset-0 bg-black bg-opacity-30 pointer-events-none"
                        initial={{
                          opacity: 0,
                        }}
                        animate={{
                          opacity: 1,
                        }}
                        exit={{
                          opacity: 0,
                        }}
                      >
                        <img src={trashSrc} alt="삭제버튼" className="m-auto" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              {/* 추가 버튼 (최대 20개까지) */}
              {uploadedImages.length < 20 && (
                <button
                  type="button"
                  onClick={handleImageSelect}
                  className="relative cursor-pointer aspect-square border-[1px] rounded-lg border-gray-200 bg-white flex items-center justify-center hover:bg-gray-50 transition-colors"
                >
                  <img src={plusCircle} alt="plusCircle" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-14 flex gap-3 px-5 py-3 bg-[#F5F5F5]">
          <Button
            className="w-full bg-[#F5F5F5]"
            style={{ pointerEvents: "auto" }}
            onClick={() => {
              if (uploadedFiles.length > 0) {
                console.log("업로드된 파일들:", uploadedFiles);

                // 로딩 시작
                toggleLoading(true);

                // API 호출
                createPhotoMutation.mutate(
                  {
                    files: uploadedFiles,
                  },
                  {
                    onSuccess: (response) => {
                      // 로딩 종료
                      toggleLoading(false);
                      console.log("사업장 이미지 업로드 성공:", response);
                      if (response.statusCode !== 200) {
                        setErrorMessage(
                          "이미지 업로드에 실패했습니다. 다시 시도해주세요."
                        );
                        setShowErrorDialog(true);
                      } else {
                        // 목록 쿼리 무효화하여 재호출
                        queryClient.invalidateQueries({
                          queryKey: ["businessCompanyPhotoList"],
                        });
                        setShowSuccessDialog(true);
                      }
                    },
                    onError: (error) => {
                      // 로딩 종료
                      toggleLoading(false);
                      console.error("사업장 이미지 업로드 실패:", error);
                      setErrorMessage(
                        "이미지 업로드에 실패했습니다. 다시 시도해주세요."
                      );
                      setShowErrorDialog(true);
                    },
                  }
                );
              }
            }}
            disabled={uploadedImages.length === 0}
          >
            <div className="py-3 mx-auto w-full text-base font-semibold text-center text-white bg-orange-400 rounded-full">
              저장하기
            </div>
          </Button>
        </div>
      </div>

      {/* 성공 Dialog */}
      <Dialog
        visible={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          navigate({ to: "/business/layout/user-business-photo-management" });
        }}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">업로드 완료</h3>
          <p className="mb-4 text-gray-600">
            사업장 이미지가 성공적으로 업로드되었습니다.
          </p>
          <button
            onClick={() => {
              setShowSuccessDialog(false);
              navigate({
                to: "/business/layout/user-business-photo-management",
              });
            }}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>

      {/* 에러 Dialog */}
      <Dialog
        visible={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">업로드 실패</h3>
          <p className="mb-4 text-gray-600">{errorMessage}</p>
          <button
            onClick={() => setShowErrorDialog(false)}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>

      {/* 파일 검증 에러 Dialog */}
      <Dialog
        visible={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">파일 형식 오류</h3>
          <p className="mb-4 text-gray-600">
            다음 파일들이 업로드되지 않았습니다:
          </p>
          <p className="mb-4 text-sm text-gray-500">
            이미지 파일만 업로드 가능합니다. (JPG, PNG, GIF, WEBP, BMP, SVG)
          </p>
          <button
            onClick={() => setShowValidationErrorDialog(false)}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>
    </>
  );
}
